import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { Media } from "@/components/forms/schemas/media";
import {
  VENUE_SECTION_OPTIONS,
  venueViewSchema,
} from "@/pages/venues/schemas/venue";
import {
  accessibilityFormSchema,
  openingHoursFormSchema,
} from "@/pages/venues/schemas/venue-facts";

const CAPACITY_MESSAGE = "Seats must be a whole number above 0";
// The backend field is an int: anything above this would fail the PUT with a 400.
const CAPACITY_MAX = 2147483647;
const CAPACITY_MAX_MESSAGE = `Seats must be at most ${CAPACITY_MAX}`;
const WIKIDATA_ID = /^Q[1-9]\d*$/;

// The AutoForm/submit contract — strict, though it derives from the lenient
// read schema. See the note on `artistEditSchema`.
export const venueEditSchema = venueViewSchema
  .omit({
    id: true,
    enrichment: true,
    enrichmentStatus: true,
    embeddingStatus: true,
    enrichmentRetryCount: true,
    createdBy: true,
    createdAt: true,
    updatedBy: true,
    updatedAt: true,
  })
  .strip()
  .extend({
    id: z
      .string()
      .readonly()
      .superRefine(
        fieldConfig({
          inputProps: {
            disabled: true,
          },
        }),
      ),
    media: Media.superRefine(
      fieldConfig({
        fieldType: "media",
        customData: {
          collection: "Venues",
        },
      }),
    ),
    // Picked from the city's areas (`area` is the host of the picker; `areaId` is what it writes).
    area: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ fieldType: "area", label: "Area" })),
    areaId: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ fieldType: "hidden" })),
    about: z
      .string()
      .nullish()
      .superRefine(
        fieldConfig({
          fieldType: "textarea",
        }),
      ),
    // Seats, an int > 0. The API leaves a null alone and has no way to clear a stored value, so
    // emptying the field changes nothing; TINA only fills it while it is empty, so a curator's
    // value wins. Not the enrichment `features.capacity` score (0-1), which stays as it is.
    capacity: z.coerce
      .number()
      .int(CAPACITY_MESSAGE)
      .min(1, CAPACITY_MESSAGE)
      .max(CAPACITY_MAX, CAPACITY_MAX_MESSAGE)
      .nullish()
      .superRefine(
        fieldConfig({
          label: "Capacity (seats)",
          description:
            "Whole number of seats. It can be changed but not cleared, and TINA only fills it while it is empty.",
        }),
      ),
    // Q + digits. The preview links it to wikidata.org; empty it to clear it.
    // Stored at `metadata.wikidataId`; `edit.tsx` diffs it on its own and sends only that key.
    metadata: z
      .object({
        wikidataId: z
          .string()
          .regex(
            WIKIDATA_ID,
            "A Wikidata item id: Q followed by digits, e.g. Q213508",
          )
          .nullish()
          .superRefine(
            fieldConfig({
              label: "Wikidata ID",
              description:
                "TINA fills it only on a safe match (Google Maps id, or the same name within 300 m) and never replaces one that is set.",
            }),
          ),
      })
      .nullish(),
    openingHours: openingHoursFormSchema,
    accessibility: accessibilityFormSchema,
    sections: z
      .array(z.enum(VENUE_SECTION_OPTIONS))
      .default([])
      .superRefine(
        fieldConfig({
          description:
            "Sections this venue belongs to. Controls which app maps it appears on (e.g. music venues map vs cinemas map).",
        }),
      ),
  });

export type VenueEditFormType = z.infer<typeof venueEditSchema>;
